import React, { useState } from 'react';
import { Icon } from '@/components/Icon';
import { Icons } from '@/assets';
import { PaginationV2 } from '@/components/PaginationV2';
import { TableV2 } from '@/components/TableV2';
import FilterDropdown from './filter-dropdown';

const TABLE_HEADERS = [
  'ID',
  'Type',
  'Date & Time',
  'Amount',
  'Payment Method',
  'Status',
  'Invoice',
];

// Fake transaction data
const FAKE_TRANSACTIONS = [
  {
    id: '1907830',
    type: 'Subscription',
    typeHint: 'Base',
    date: 'Aug 02, 2025', // Today
    amount: 49.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    invoice: 'invoice-001.pdf',
  },
  {
    id: '1907832',
    type: 'Upgrade',
    date: 'Aug 01, 2025', // Yesterday
    amount: 29.99,
    paymentMethod: 'PayPal',
    status: 'Paid',
    invoice: 'invoice-002.pdf',
  },
  {
    id: '1907833',
    type: 'Subscription',
    date: 'Jul 28, 2025', // This week
    amount: 49.99,
    paymentMethod: 'Credit Card',
    status: 'Failed',
    typeHint: 'Premium',
    invoice: null,
  },
  {
    id: '1907834',
    type: 'Add-on',
    date: 'Jul 15, 2025', // This month
    amount: 19.99,
    paymentMethod: 'Bank Transfer',
    status: 'Paid',
    invoice: 'invoice-004.pdf',
  },
  {
    id: '1907835',
    type: 'Subscription',
    date: 'Jun 03, 2025', // Last month
    amount: 49.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    invoice: 'invoice-005.pdf',
  },
  {
    id: '1907836',
    type: 'Refund',
    date: 'Jun 10, 2025', // Last month
    amount: -29.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    typeHint: 'Base',
    invoice: 'refund-006.pdf',
  },
  {
    id: '1907837',
    type: 'Subscription',
    date: 'Jan 15, 2025', // This year
    amount: 49.99,
    typeHint: 'Base',
    paymentMethod: 'PayPal',
    status: 'Failed',
    invoice: null,
  },
  {
    id: '1907838',
    type: 'Upgrade',
    date: 'Mar 22, 2024', // Last year
    amount: 39.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    invoice: 'invoice-008.pdf',
  },
  {
    id: '1907830',
    type: 'Subscription',
    date: 'Aug 02, 2025', // Today
    amount: 49.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    invoice: 'invoice-001.pdf',
  },
  {
    id: '1907832',
    type: 'Upgrade',
    date: 'Aug 01, 2025', // Yesterday
    amount: 29.99,
    paymentMethod: 'PayPal',
    status: 'Paid',
    invoice: 'invoice-002.pdf',
  },
  {
    id: '1907833',
    type: 'Subscription',
    date: 'Jul 28, 2025', // This week
    amount: 49.99,
    paymentMethod: 'Credit Card',
    status: 'Failed',
    invoice: null,
  },
  {
    id: '1907834',
    type: 'Add-on',
    date: 'Jul 15, 2025', // This month
    amount: 19.99,
    paymentMethod: 'Bank Transfer',
    status: 'Paid',
    invoice: 'invoice-004.pdf',
  },
  {
    id: '1907835',
    type: 'Subscription',
    date: 'Jun 03, 2025', // Last month
    amount: 49.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    invoice: 'invoice-005.pdf',
  },
  {
    id: '1907836',
    type: 'Refund',
    date: 'Jun 10, 2025', // Last month
    amount: -29.99,
    paymentMethod: 'Credit Card',
    status: 'Paid',
    invoice: 'refund-006.pdf',
  },
];

const ITEMS_PER_PAGE = 7;

export const TransactionsTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState({
    date: [],
    type: [],
  });

  // Helper function to check if a date matches the filter criteria
  const isDateInRange = (dateString, filterKey) => {
    const transactionDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1
    );
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

    const yearStart = new Date(today.getFullYear(), 0, 1);
    const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
    const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);

    switch (filterKey) {
      case 'today':
        return transactionDate >= today;
      case 'yesterday':
        return transactionDate >= yesterday && transactionDate < today;
      case 'thisWeek':
        return transactionDate >= weekStart;
      case 'thisMonth':
        return transactionDate >= monthStart;
      case 'lastMonth':
        return (
          transactionDate >= lastMonthStart && transactionDate <= lastMonthEnd
        );
      case 'thisYear':
        return transactionDate >= yearStart;
      case 'lastYear':
        return (
          transactionDate >= lastYearStart && transactionDate <= lastYearEnd
        );
      default:
        return true;
    }
  };

  // Filter transactions based on applied filters
  const getFilteredTransactions = () => {
    let filtered = FAKE_TRANSACTIONS;

    // Apply date filters
    if (appliedFilters.date.length > 0) {
      filtered = filtered.filter((transaction) =>
        appliedFilters.date.some((filterKey) =>
          isDateInRange(transaction.date, filterKey)
        )
      );
    }

    // Apply type filters
    if (appliedFilters.type.length > 0) {
      filtered = filtered.filter((transaction) => {
        const transactionType = transaction.type.toLowerCase();
        return appliedFilters.type.some((filterKey) => {
          if (filterKey === 'subscription') {
            return transactionType === 'subscription';
          }
          if (filterKey === 'streetturn') {
            // Map other transaction types to "streetturn" category
            return ['upgrade', 'add-on', 'refund'].includes(transactionType);
          }
          return false;
        });
      });
    }

    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  // Handle filter application
  const handleFiltersApply = (newFilters) => {
    setAppliedFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Handle removing individual filters
  const removeFilter = (filterType, filterValue) => {
    const newFilters = { ...appliedFilters };
    newFilters[filterType] = newFilters[filterType].filter(
      (item) => item !== filterValue
    );
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return appliedFilters.date.length > 0 || appliedFilters.type.length > 0;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'bg-success-light text-success';
      case 'failed':
        return 'bg-error-light text-error';
      default:
        return 'bg-gray-100 text-inactive-text';
    }
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const sign = amount < 0 ? '-' : '';
    return `${sign}$${Math.abs(amount).toFixed(2)}`;
  };

  const handleDownloadInvoice = (invoiceFile) => {
    console.log(`Downloading ${invoiceFile}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const prepareTableData = () => {
    return currentTransactions.map((item) => ({
      id: item.id,
      cellData: [
        item.id || '-',
        <span>
          {item.type || '-'}
          <span className="text-inactive-text text-xs">
            {item.typeHint ? ` (${item.typeHint})` : ''}
          </span>
        </span>,
        item.date,
        formatAmount(item.amount),
        <div className="flex justify-center">
          <span className="w-32 font-medium flex items-center h-5 px-3 justify-start rounded-full">
            <Icon
              className="mr-2 flex-shrink-0"
              src={item.paymentMethod === 'PayPal' ? Icons.payPal : Icons.visa}
              width="24px"
              height="16px"
            />
            {item.paymentMethod}
          </span>
        </div>,
        <span
          className={`font-bold h-5 mx-auto px-3 w-fit flex items-center justify-center rounded-full ${getStatusColor(
            item.status
          )}`}>
          <div
            className={`w-1 h-1 rounded-full mr-2 flex-shrink-0 ${
              item.status?.toLowerCase() === 'paid'
                ? 'bg-success'
                : item.status?.toLowerCase() === 'failed'
                ? 'bg-error'
                : 'bg-inactive-text'
            }`}
          />
          {item.status || 'Unknown'}
        </span>,
        item.invoice ? (
          <button
            onClick={() => handleDownloadInvoice(item.invoice)}
            className="flex mx-auto cursor-pointer border-none text-light-blue shadow-none bg-transparent items-center justify-center px-3 py-1 text-xs hover:text-primary-dark transition-colors"
            title="Download Invoice">
            <Icon
              src={Icons.download}
              width="14px"
              height="14px"
              className="mr-1"
            />
            View Invoice
          </button>
        ) : (
          <span className="text-gray-400 text-xs text-center mx-auto">-</span>
        ),
      ],
    }));
  };

  return (
    <div className="w-full">
      <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row items-end justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-primary-text m-0">
            Your Transactions
          </h1>
          <p className="text-secondary-text text-lg m-0 mt-2">
            A brief summary of the transactions will be displayed here
          </p>
        </div>
        <FilterDropdown
          onFiltersApply={handleFiltersApply}
          appliedFilters={appliedFilters}
          hasActiveFilters={hasActiveFilters()}
        />
      </div>
      {hasActiveFilters() && (
        <div className="flex items-center mb-7">
          <span className="text-sm font-bold mr-3">Filtered by:</span>

          <div className="flex items-center gap-2">
            {appliedFilters.date.map((filterKey) => {
              const getDateLabel = (filterKey) => {
                switch (filterKey) {
                  case 'today':
                    return 'Today';
                  case 'yesterday':
                    return 'Yesterday';
                  case 'thisWeek':
                    return 'This Week';
                  case 'thisMonth':
                    return 'This Month';
                  case 'lastMonth':
                    return 'Last Month';
                  case 'thisYear':
                    return 'This Year';
                  case 'lastYear':
                    return 'Last Year';
                  default:
                    return filterKey;
                }
              };

              return (
                <span
                  key={filterKey}
                  className="border-inactive-text border text-inactive-text text-xs border-solid rounded-full font-medium  px-2 h-7 flex items-center gap-1 justify-center">
                  {getDateLabel(filterKey)}
                  <button
                    onClick={() => removeFilter('date', filterKey)}
                    className="ml-1 text-border-secondary hover:text-gray-700 cursor-pointer bg-transparent border-none p-0 flex items-center justify-center"
                    title="Remove filter">
                    <Icon src={Icons.xIconBlack} width="12px" height="12px" />
                  </button>
                </span>
              );
            })}

            {appliedFilters.type.map((filterKey) => {
              const getTypeLabel = (filterKey) => {
                switch (filterKey) {
                  case 'subscription':
                    return 'Subscription';
                  case 'streetturn':
                    return 'StreetTurn';
                  default:
                    return filterKey;
                }
              };

              return (
                <span
                  key={filterKey}
                  className="border-inactive-text px-2 h-7 border text-inactive-text text-xs border-solid rounded-full font-medium flex items-center gap-1 justify-center">
                  {getTypeLabel(filterKey)}
                  <button
                    onClick={() => removeFilter('type', filterKey)}
                    className="ml-1 text-border-secondary hover:text-gray-700 cursor-pointer bg-transparent border-none p-0 flex items-center justify-center"
                    title="Remove filter">
                    <Icon src={Icons.xIconBlack} width="12px" height="12px" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      )}
      {currentTransactions && currentTransactions.length > 0 ? (
        <div className="mb-8">
          <TableV2
            headers={TABLE_HEADERS}
            data={prepareTableData()}
            emptyMessage="No transactions available"
          />

          <PaginationV2
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={filteredTransactions.length}
            itemsPerPage={ITEMS_PER_PAGE}
            showInfo={true}
          />
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">No transactions available</p>
        </div>
      )}
    </div>
  );
};
